import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Users, Headphones, Heart } from "lucide-react";

const team = [
  {
    role: "Lead Engineer",
    experience: "15+ years",
    description: "Our head engineer brings over 15 years of professional industry experience, having worked with artists across all genres from hip-hop to rock, R&B to electronic."
  },
  {
    role: "Production Team",
    experience: "Combined 20+ years",
    description: "Our production team consists of skilled beatmakers, composers, and sound designers who can bring any musical vision to life."
  },
  {
    role: "Photography Team",
    experience: "Professional",
    description: "Experienced photographers specializing in artist portraits, album artwork, and promotional content."
  }
];

const values = [
  { icon: Award, title: "Quality First", description: "We never compromise on sound quality. Every session meets professional standards." },
  { icon: Users, title: "Artist-Focused", description: "Your vision is our priority. We're here to support and enhance your creativity." },
  { icon: Headphones, title: "All Genres Welcome", description: "Hip-hop, rock, pop, electronic, classical—we've worked with them all." },
  { icon: Heart, title: "Passion-Driven", description: "We love what we do, and it shows in every project we touch." },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function About() {
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">About Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              The Dream Makers Story
            </h1>
            <p className="text-white/60 text-lg">
              Established in 2024, Dream Makers Recording Group was born from a passion 
              for helping artists create their best work in a professional yet inspiring environment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-dream-surface flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/3zpgj9id_MUSIC%20ACADEMY%20-%205.png"
                  alt="Dream Makers Recording Group"
                  className="w-3/4 h-auto object-contain"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Built for Artists,<br />By Artists
              </h2>
              <div className="space-y-4 text-white/60">
                <p>
                  Dream Makers Recording Group is more than just a studio—it's a creative hub 
                  designed specifically for rappers, singers, bands, DJs, producers, and 
                  independent artists of all genres.
                </p>
                <p>
                  Located in Tullamarine, Melbourne, our space was built from the ground up 
                  with one goal in mind: to provide artists with the professional tools, 
                  expert guidance, and inspiring atmosphere needed to bring their creative 
                  visions to life.
                </p>
                <p>
                  With an experienced in-house engineer bringing over 15 years of professional 
                  industry experience, and a highly skilled support team, we're committed to 
                  delivering quality, creativity, and results at the highest level.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Our Values</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="glow-card p-8 rounded-2xl text-center"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-dream-violet/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-dream-violet" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-white/60 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Our Team</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              The People Behind the Sound
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.role}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="glow-card p-8 rounded-2xl"
                data-testid={`team-member-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-dream-violet/20 flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-dream-violet" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-1">{member.role}</h3>
                <p className="text-dream-violet font-mono text-sm mb-4">{member.experience}</p>
                <p className="text-white/60 text-sm">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Create Something Amazing?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">
              Join the Dream Makers family and let's bring your music to life together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="bg-white text-black hover:bg-dream-violet hover:text-white rounded-full px-8 py-6 font-bold transition-all">
                  Book Your Session
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet rounded-full px-8 py-6 bg-transparent"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
